import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core'
import { MatListOption } from '@angular/material/list'
import { IBvUser } from '@modules'
import { IconsModule } from '@ziptility/icons'
import { AuthorizationService, CommonService, NavigationService, TranslationService } from '@ziptility/services'
import { first } from 'lodash/fp'
import { Attachment, Task, TaskStatus } from '@ziptility/graphql'
import { RouterModule } from '@angular/router'
import { BoardTask } from '@ziptility/tasks/boards/boards.store'
import { UserAvatarComponent } from '../user-avatar'
import { TaskBoardBadgeComponent } from '../task-board-badge/task-board-badge.component'

@Component({
  selector: 'task-list-item',
  template: `
    @let task = this.task();

    <div class="px-4 py-3 flex border-b border-gray-200 relative container hover:bg-gray-50 bg-white"
      [class.selected]="selected()"
    >
      <div class="task-details ">
        <div class="flex flex-{{ flexDirection() }} {{ containerClasses }} ">
          <div class="task-details_left">
            <div class="flex flex-col space-y-1 items-start mb-1">
              <div
                class="task-status-{{ task.status }} text-sm font-semibold"
                [class.line-through]="isResolved() && showCompletedTextDecoration()"
                [class.muted]="!isActive()"
              >
                {{ task.title }}
              </div>
              @if (locationIdentifier()) {
                <div class="text-slate-700 text-xs">{{ locationIdentifier() }}</div>
              }
            </div>

            @if (showThumbnails() && thumbnail()) {
              <div
                class="flex items-center overflow-auto rounded-lg w-[100px] h-[100px]  bg-center bg-no-repeat bg-cover"
                [ngStyle]="{ 'background-image': 'url(' + getUrlForImage(thumbnail().id) + ')' }"
              ></div>
            }

            <div class="flex flex-wrap items-center">
              <div class="flex flex-wrap items-center">
                @switch (!!task.dateCompleted) {
                  @case (true) {
                    <div class="rounded-full py-1 px-3 mr-2 flex items-center text-sm bg-gray-200 text-gray-500">
                      <div
                        class="rounded-full bg-gray-500 w-[24px] h-[24px] text-white flex items-center justify-center mr-2"
                      >
                        <i-feather name="check" style="width: 16px; height: 16px;"></i-feather>
                      </div>
                      {{ task.dateCompleted | date: 'EEE, MMM d yyyy' }}
                    </div>
                    @if (task.completor; as completor) {
                      <user-avatar
                        [firstName]="completor.firstName"
                        [lastName]="completor.lastName"
                        [displayStyle]="'full'"
                      ></user-avatar>
                    }
                  }
                  @case (false) {
                    <div class="text-xs" (click)="onSelectBoard($event)">
                      @if (showBoard() && task.boardId) {
                        <task-board-badge [boardId]="task.boardId"></task-board-badge>
                      }
                      @if (!!task.dueDate) {
                        <div class="inline-block">
                          <div
                            class="due-date-badge {{
                              task.dueDateUrgency
                            }} flex space-x-1 items-start rounded p-1 my-1 bg-gray-200"
                          >
                            <i-feather name="calendar" class="feather-icon-sm"></i-feather>
                            <div class="text-xs">
                              {{ task.formattedDueDate }}
                            </div>
                          </div>
                        </div>
                      }
                      <div class="mt-1 text-[#AAB2B7]">
                        {{ task.formattedDateLabel }}
                      </div>
                    </div>
                  }
                }
              </div>
            </div>
          </div>
        </div>
      </div>
      @if (showSelection()) {
        <input
          type="checkbox"
          class="absolute top-3 right-8 cursor-pointer opacity-0 hover:opacity-100"
          (click)="$event.stopPropagation(); onSelection.emit({ selected: !selected(), task: task })"
          [checked]="selected()"
        />
      }
      <div class="text-gray-500 text-xs absolute top-3 right-3">
        {{ order() }}
      </div>
    </div>
  `,
  styles: [
    `
      .container.selected input[type='checkbox'] {
        opacity: 1;
      }

      .due-date-badge.pastDue {
        background-color: #ffe7e5;
        color: theme('colors.red.brand');
        display: flex;
        gap: theme('spacing.1');
        align-items: center;
        border-radius: theme('borderRadius.md');
        padding: theme('spacing.1');
        margin: theme('spacing.1') 0;
      }
      .due-date-badge.notDue,
      .due-date-badge.dueToday {
        background-color: #e7e9ea;
        color: theme('colors.gray.500');
        display: flex;
        gap: theme('spacing.1');
        align-items: center;
        border-radius: theme('borderRadius.md');
        padding: theme('spacing.1');
        margin: theme('spacing.1');
      }
    `,
  ],
  imports: [CommonModule, RouterModule, IconsModule, UserAvatarComponent, TaskBoardBadgeComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskListItemComponent {
  readonly authorizationService = inject(AuthorizationService)
  readonly commonService = inject(CommonService)
  readonly navigationService = inject(NavigationService)
  readonly translationService = inject(TranslationService)

  readonly showThumbnails = input<boolean>(true)
  readonly order = input<number | undefined>(undefined)
  readonly task = input.required<BoardTask>()
  readonly showSubtitle = input<boolean>(true)
  readonly showBoard = input<boolean>(false)
  readonly user = input<IBvUser>()
  readonly users = input<IBvUser[]>([])
  readonly showSelection = input<boolean>(false)
  readonly selected = input<boolean>(false)
  readonly isActive = input<boolean>(true)
  readonly showCompletedTextDecoration = input<boolean>(true)
  readonly flexDirection = input<'row' | 'col'>('row')

  readonly thumbnail = computed<Attachment | undefined>(() => this.task().thumbnail ?? first(this.task().attachments))
  readonly locationIdentifier = computed<string | undefined>(
    () => this.task().locationIdentifier ?? this.task().asset?.locationIdentifier,
  )
  readonly isResolved = computed<boolean>(() => this.task().status == TaskStatus.Resolved)

  readonly onListToggle = output<{
    listId: string
    taskId: string
    selected: boolean
  }>()

  readonly onSelection = output<{
    selected: boolean
    task: Task
  }>()

  get containerClasses() {
    if (this.flexDirection() == 'row') {
      return 'justify-between items-center'
    }
    return ''
  }

  onSelectionChange($event) {
    const option: MatListOption = $event.option
    this.onListToggle.emit({ listId: option.value, taskId: this.task().id, selected: option.selected })
  }

  getUrlForImage(id: string): string | undefined {
    return `https://api.ziptility.com/image-server/ziptility.assets/${id}/:/rs=w:500`
  }

  onSelectBoard($event) {
    $event.stopPropagation()
    this.navigationService.navigateToTaskBoards()
  }
}
